import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'

import { buildPortalDetailRoute, getPortalConfig, normalizePortal } from 'utils/portal'
import { CoreContext } from 'context/CoreContext'
import { languages } from 'utils/options'
import useI18n from 'hooks/useI18n'

import { adapters } from 'utils/adapters'
import {
  exposeStrapiError,
  isModularContentType,
  normalizeStrapiRegister,
} from 'utils'

import { Read } from 'services/core'
import { buildS3DownloadUrl } from 'services/s3Download'
import { trackEvent } from 'services/analytics'
import { isContentLockedError } from 'utils/lockedContent'
import {
  completeLesson as completeLessonRequest,
  readCourseDetails,
  toggleLessonFavorite,
} from 'services/course-details'
import {
  CompleteLearningPathItem,
  ReadLearningPathNavigation,
  StartLearningPathItem,
} from 'services/learning-paths'
import { canOfferSurveyAfterCourse } from './survey-end'

const transcriptLanguageByLocale = {
  en: 'eng',
  'en-US': 'eng',
  'en-GB': 'eng',
  pt: 'por',
  'pt-BR': 'por',
  es: 'spa',
  de: 'deu',
  fr: 'fra',
  it: 'ita',
  ja: 'jpn',
  hi: 'hin',
  zh: 'zho',
  'zh-CN': 'zh-Hans',
  'zh-Hans': 'zh-Hans',
  'zh-Hant': 'zh-Hant',
}

function resolveTranscriptLanguage(locale) {
  if (!locale) return 'eng'
  return transcriptLanguageByLocale[locale]
    || transcriptLanguageByLocale[String(locale).split('-')[0]]
    || locale
}

function useTrackOnce() {
  const sentRef = useRef(new Set())

  return useCallback((key, fn) => {
    if (sentRef.current.has(key)) return
    fn()
    sentRef.current.add(key)
  }, [])
}

function getQuizHint({
  hasQuiz,
  approved,
  canGetCertificate,
  completed,
  surveyRequired,
  surveyRequiredMessage,
  certificateApproval,
}) {
  if (surveyRequired) {
    return surveyRequiredMessage
  }
  if (['pending', 'pending_trainer_approval', 'processing'].includes(certificateApproval?.status)) {
    return 'Your certificate is awaiting manual training approval.'
  }
  if (certificateApproval?.status === 'rejected') {
    return 'Your training approval was rejected. Contact the training team for details.'
  }
  if (canGetCertificate) {
    return approved
      ? 'Quiz approved. You can now download your certificate.'
      : 'Training approved. You can now download your certificate.'
  }
  if (!hasQuiz) {
    return null
  }
  if (!completed) {
    return 'Complete this course to start the test.'
  }
  if (approved && !canGetCertificate) {
    return 'Quiz approved. Your certificate is being prepared.'
  }
  return 'Complete the quiz to unlock your certificate.'
}

function recomputeModules(modules = []) {
  return modules.map(module => {
    const completedLessons = (module?.lessons || []).filter(lesson => lesson?.isCompleted).length
    const lessonCount = (module?.lessons || []).length

    return {
      ...module,
      completedLessons,
      lessonCount,
      completed: lessonCount > 0 && completedLessons >= lessonCount,
    }
  })
}

function recomputeDetails(detail) {
  if (!detail) return detail

  const modules = recomputeModules(detail?.modules || [])
  const totalLessons = modules.reduce((sum, module) => sum + (module?.lessonCount || 0), 0)
  const completedLessons = modules.reduce((sum, module) => sum + (module?.completedLessons || 0), 0)

  return {
    ...detail,
    modules,
    moduleCount: modules.length,
    totalLessons,
    completedLessons,
    completionPercent: totalLessons ? Math.round((completedLessons / totalLessons) * 100) : 0,
  }
}

function buildResources(lesson) {
  if (!lesson) return []

  const resources = []
  const lessonFile = lesson?.file || lesson?.document || null
  const rawFileUrl = typeof lessonFile === 'string' ? lessonFile : lessonFile?.url
  const fileUrl = typeof rawFileUrl === 'string' ? rawFileUrl.trim() : rawFileUrl
  const externalUrl = typeof lesson?.external_url === 'string'
    ? lesson.external_url.trim()
    : lesson?.external_url
  const hasFile = !!fileUrl

  if (hasFile) {
    resources.push({
      id: `file-${lesson.id}`,
      type: 'file',
      title: lessonFile?.name || lesson?.title || 'Course file',
      description: 'Download the lesson attachment.',
      file: lessonFile,
    })
  }

  if (externalUrl) {
    resources.push({
      id: `link-${lesson.id}`,
      type: 'link',
      title: lesson?.external_label || 'Open external resource',
      description: externalUrl,
      url: externalUrl,
    })
  }

  return resources
}

function buildModuleResources(module) {
  const resources = Array.isArray(module?.resources) ? module.resources : []

  return resources
    .map((file, index) => {
      const rawFileUrl = typeof file === 'string' ? file : file?.url
      const fileUrl = typeof rawFileUrl === 'string' ? rawFileUrl.trim() : rawFileUrl
      if (!fileUrl) return null

      return {
        id: `module-resource-${module?.id || 'module'}-${file?.id || index}`,
        type: 'file',
        title: file?.name || file?.alternativeText || `Module resource ${index + 1}`,
        description: file?.caption || 'Download the module attachment.',
        file: typeof file === 'string' ? fileUrl : file,
      }
    })
    .filter(Boolean)
}

function normalizeLegacyCourse(type, raw, locale) {
  const adapter = adapters?.[type]
  if (adapter) return adapter(raw, locale)

  return {
    ...raw,
    type: raw?.type || 'article',
  }
}

export default function useController(portal) {
  const { t } = useI18n()
  const history = useHistory()
  const location = useLocation()
  const { language, setModal, user } = useContext(CoreContext)
  const locale = useMemo(() => languages.find((item) => item.id === language)?.ref || 'en', [language])
  const transcriptLanguage = useMemo(() => resolveTranscriptLanguage(locale), [locale])
  const portalConfig = useMemo(() => getPortalConfig(normalizePortal(portal)), [portal])
  const handleBackToCourses = useCallback(() => {
    if (history.length > 1) {
      history.goBack()
      return
    }

    history.push(portalConfig.homeRoute)
  }, [history, portalConfig.homeRoute])
  const { id, type, lesson } = useParams()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const trailContextParams = useMemo(() => ({
    trailId: searchParams.get('trailId'),
    stepId: searchParams.get('stepId'),
    itemId: searchParams.get('itemId'),
  }), [searchParams])
  const showModularFlow = isModularContentType(type)
  const trackOnce = useTrackOnce()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [detail, setDetail] = useState(null)
  const [legacyCourse, setLegacyCourse] = useState(null)
  const [selectedLessonId, setSelectedLessonId] = useState(lesson || null)
  const [surveyPromptVisible, setSurveyPromptVisible] = useState(false)
  const [trailContext, setTrailContext] = useState(null)
  const completedTrailItemsRef = useRef(new Set())

  const contentConfig = useMemo(
    () => ({
      courses: {
        showQuiz: true,
        analyticsPrefix: 'course',
      },
      'partners-and-staffs': {
        showQuiz: false,
        analyticsPrefix: 'partners_and_staff',
      },
    }),
    []
  )

  const loadModularDetails = useCallback(async () => {
    if (!showModularFlow || !id) return

    setLoading(true)
    setError('')
    try {
      const result = await readCourseDetails(type, id, locale)

      if (isContentLockedError(result) && normalizePortal(portal) === 'essentials') {
        setModal({ type: 'exclusive-content' })
        history.replace(portalConfig.homeRoute)
      } else if (exposeStrapiError(result)) {
        setDetail(null)
        setSelectedLessonId(null)
        setError('This course could not be loaded. Please try again.')
      } else {
        const nextDetail = recomputeDetails(result?.data || null)
        if (!nextDetail) {
          setDetail(null)
          setSelectedLessonId(null)
          setError('This course could not be loaded. Please try again.')
          return
        }

        setDetail(nextDetail)
        const firstLessonId = lesson
          || nextDetail.modules?.flatMap(module => module?.lessons || []).find(item => `${item?.id}` === `${lesson}`)?.id
          || nextDetail.modules?.[0]?.lessons?.[0]?.id
          || null
        setSelectedLessonId(firstLessonId)
      }
    } catch {
      setDetail(null)
      setSelectedLessonId(null)
      setError('This course could not be loaded. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [history, id, lesson, locale, portal, portalConfig.homeRoute, setModal, showModularFlow, type])

  const loadLegacyDetails = useCallback(async () => {
    if (showModularFlow || !type || !id) return

    setLoading(true)
    setError('')
    try {
      const result = await Read(type, `/${id}?populate=*`)
      if (exposeStrapiError(result)) {
        setLegacyCourse(null)
        setError('This course could not be loaded. Please try again.')
      } else {
        const normalized = normalizeStrapiRegister(result) || result?.data || null
        if (!normalized) {
          setLegacyCourse(null)
          setError('This course could not be loaded. Please try again.')
          return
        }
        setLegacyCourse(normalizeLegacyCourse(type, normalized, locale))
      }
    } catch {
      setLegacyCourse(null)
      setError('This course could not be loaded. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [id, locale, showModularFlow, type])

  useEffect(() => {
    if (showModularFlow) {
      loadModularDetails()
      return
    }

    setDetail(null)
    setSelectedLessonId(null)
    loadLegacyDetails()
  }, [loadLegacyDetails, loadModularDetails, showModularFlow])

  useEffect(() => {
    if (!trailContextParams.trailId || !trailContextParams.itemId) {
      setTrailContext(null)
      return
    }

    let mounted = true
    async function loadTrailContext() {
      const result = await ReadLearningPathNavigation(trailContextParams.trailId, {
        portal: normalizePortal(portal),
        locale,
        stepId: trailContextParams.stepId,
        itemId: trailContextParams.itemId,
      })
      if (!mounted) return
      if (!exposeStrapiError(result)) {
        setTrailContext({
          trailId: trailContextParams.trailId,
          stepId: trailContextParams.stepId,
          itemId: trailContextParams.itemId,
          trailTitle: result?.data?.trail?.title,
          previousItem: result?.data?.previousItem,
          nextItem: result?.data?.nextItem,
        })
      }
    }

    loadTrailContext()
    return () => {
      mounted = false
    }
  }, [
    locale,
    portal,
    trailContextParams.itemId,
    trailContextParams.stepId,
    trailContextParams.trailId,
  ])

  const selectedLesson = useMemo(() => {
    if (!showModularFlow) return legacyCourse
    const lessons = detail?.modules?.flatMap(module => module?.lessons || []) || []
    return lessons.find(item => `${item?.id}` === `${selectedLessonId || lesson}`) || lessons[0] || null
  }, [detail?.modules, legacyCourse, lesson, selectedLessonId, showModularFlow])

  const selectedModule = useMemo(() => {
    if (!showModularFlow || !selectedLesson?.id) return null
    return detail?.modules?.find(module => module?.id === selectedLesson?.moduleId)
      || detail?.modules?.find(module => module?.lessons?.some(item => item?.id === selectedLesson?.id))
      || null
  }, [detail?.modules, selectedLesson?.id, selectedLesson?.moduleId, showModularFlow])

  const currentQuiz = useMemo(() => {
    if (!showModularFlow || !selectedModule) return null

    const quiz = selectedModule?.quiz || {}
    const hasQuiz = !!quiz?.hasQuiz
    const approved = !!quiz?.approved
    const canGetCertificate = !!quiz?.canGetCertificate
    const surveyRequired = !!quiz?.surveyRequired
    const certificateApproval = quiz?.certificateApproval || null

    return {
      ...quiz,
      hasQuiz,
      approved,
      canStartTest: typeof quiz?.canStartTest === 'boolean'
        ? quiz.canStartTest
        : hasQuiz && selectedModule?.completed && !approved && !surveyRequired,
      canGetCertificate,
      certificateApproval,
      hint: getQuizHint({
        hasQuiz,
        approved,
        canGetCertificate,
        completed: !!selectedModule?.completed,
        surveyRequired,
        surveyRequiredMessage: t('survey_required_before_quiz'),
        certificateApproval,
      }),
    }
  }, [selectedModule, showModularFlow, t])

  const resources = useMemo(() => buildResources(selectedLesson), [selectedLesson])
  const moduleResources = useMemo(() => buildModuleResources(selectedModule), [selectedModule])
  const currentSurvey = useMemo(
    () => showModularFlow && type === 'courses' ? detail?.survey || null : null,
    [detail?.survey, showModularFlow, type]
  )
  const canPromptSurveyAfterVideo = useMemo(() => canOfferSurveyAfterCourse({
    survey: currentSurvey,
    lesson: selectedLesson,
    modules: detail?.modules,
  }), [currentSurvey, detail?.modules, selectedLesson])

  useEffect(() => {
    setSurveyPromptVisible(false)
  }, [currentSurvey?.completed, selectedLesson?.id])

  const handleOpenSurvey = useCallback(() => {
    if (!currentSurvey || !detail?.id) return
    history.push(portalConfig.buildSurveyRoute(detail.id))
  }, [currentSurvey, detail?.id, history, portalConfig])

  const handleOpenSurveyFromPrompt = useCallback(() => {
    trackEvent('survey_end_prompt_opened', {
      content_type: 'survey',
      content_id: currentSurvey?.id,
      content_name: currentSurvey?.title,
      course_id: detail?.id,
      course_name: detail?.title,
      source: 'course_video_end',
    })
    handleOpenSurvey()
  }, [currentSurvey, detail?.id, detail?.title, handleOpenSurvey])

  const handleDismissSurveyPrompt = useCallback(() => {
    setSurveyPromptVisible(false)
    trackEvent('survey_end_prompt_dismissed', {
      content_type: 'survey',
      content_id: currentSurvey?.id,
      content_name: currentSurvey?.title,
      course_id: detail?.id,
      course_name: detail?.title,
      source: 'course_video_end',
    })
  }, [currentSurvey, detail?.id, detail?.title])

  const mutateLesson = useCallback((moduleId, lessonId, updater) => {
    setDetail(prev => {
      if (!prev) return prev

      const nextModules = (prev?.modules || []).map(module => {
        if (`${module?.id}` !== `${moduleId}`) return module

        return {
          ...module,
          lessons: (module?.lessons || []).map(item => (
            `${item?.id}` !== `${lessonId}` ? item : updater(item)
          )),
        }
      })

      return recomputeDetails({
        ...prev,
        modules: nextModules,
      })
    })
  }, [])

  const handleToggleFavorite = useCallback(async (moduleId, lessonItem) => {
    if (!showModularFlow || !lessonItem?.id || !moduleId) return

    const nextActive = !lessonItem?.isFavorite

    mutateLesson(moduleId, lessonItem.id, item => ({
      ...item,
      isFavorite: nextActive,
      favoriteId: nextActive ? item?.favoriteId || 'pending' : null,
    }))

    const result = await toggleLessonFavorite(type, moduleId, lessonItem.id)
    if (exposeStrapiError(result)) {
      mutateLesson(moduleId, lessonItem.id, item => ({
        ...item,
        isFavorite: lessonItem?.isFavorite,
        favoriteId: lessonItem?.favoriteId || null,
      }))
      return
    }

    const favoriteEntry = result?.data?.favorite
    mutateLesson(moduleId, lessonItem.id, item => ({
      ...item,
      isFavorite: !!result?.data?.active,
      favoriteId: favoriteEntry?.id || null,
    }))
  }, [mutateLesson, showModularFlow, type])

  const handleCompleteLesson = useCallback(async (moduleId, lessonId) => {
    if (!showModularFlow || !moduleId || !lessonId) return false

    const currentLesson = detail?.modules
      ?.find(module => `${module?.id}` === `${moduleId}`)
      ?.lessons?.find(item => `${item?.id}` === `${lessonId}`)

    if (currentLesson?.isCompleted) return true

    mutateLesson(moduleId, lessonId, item => ({
      ...item,
      isCompleted: true,
    }))

    const result = await completeLessonRequest(type, moduleId, lessonId)
    if (exposeStrapiError(result)) {
      mutateLesson(moduleId, lessonId, item => ({
        ...item,
        isCompleted: false,
      }))
      return false
    }

    return true
  }, [detail?.modules, mutateLesson, showModularFlow, type])

  useEffect(() => {
    if (!trailContextParams.trailId || !trailContextParams.itemId || !detail?.completionPercent) return
    if (detail.completionPercent < 100) return

    const key = `${trailContextParams.trailId}:${trailContextParams.itemId}`
    if (completedTrailItemsRef.current.has(key)) return
    completedTrailItemsRef.current.add(key)

    CompleteLearningPathItem(trailContextParams.trailId, trailContextParams.itemId, {
      portal: normalizePortal(portal),
      locale,
    }).then(result => {
      if (exposeStrapiError(result)) {
        completedTrailItemsRef.current.delete(key)
        return
      }
      setTrailContext(prev => ({
        ...prev,
        nextItem: result?.data?.nextItem || prev?.nextItem,
        previousItem: result?.data?.previousItem || prev?.previousItem,
      }))
    })
  }, [
    detail?.completionPercent,
    locale,
    portal,
    trailContextParams.itemId,
    trailContextParams.trailId,
  ])

  const buildTrailItemRoute = useCallback((item) => {
    if (!item) return null
    if (item?.type !== 'courses') return `${portalConfig.basePath}/trails/${trailContextParams.trailId}`

    const params = new URLSearchParams()
    params.set('trailId', trailContextParams.trailId)
    if (item?.stepId) params.set('stepId', item.stepId)
    if (item?.itemId) params.set('itemId', item.itemId)

    return `${buildPortalDetailRoute(portal, item.type, item.contentId || item.id)}?${params.toString()}`
  }, [portal, portalConfig.basePath, trailContextParams.trailId])

  const navigateTrailItem = useCallback(async (item) => {
    if (!item) return
    if (trailContextParams.trailId && item?.itemId) {
      await StartLearningPathItem(trailContextParams.trailId, item.itemId, {
        portal: normalizePortal(portal),
        locale,
      })
    }

    if (item?.type === 'documents' && item?.action?.value) {
      trackEvent('PDF_OPEN', {
        content_type: item?.type,
        content_id: item?.contentId || item?.id,
        resource_id: item?.itemId,
        content_name: item?.title,
        course_id: detail?.id,
        learning_path_id: trailContextParams.trailId,
      })
      setModal({
        type: 'pdf-preview',
        title: item?.title,
        url: buildS3DownloadUrl({
          sourceUrl: item.action.value,
          filename: item?.title || item?.file || item?.document || 'document.pdf',
          disposition: 'inline',
        }),
      })
      return
    }

    const route = buildTrailItemRoute(item)
    if (route) history.push(route)
  }, [buildTrailItemRoute, detail?.id, history, locale, portal, setModal, trailContextParams.trailId])

  const handleBackToTrail = useCallback(() => {
    if (!trailContextParams.trailId) return
    history.push(`${portalConfig.basePath}/trails/${trailContextParams.trailId}`)
  }, [history, portalConfig.basePath, trailContextParams.trailId])

  useEffect(() => {
    if (!showModularFlow || !selectedLesson?.id || !selectedModule?.id) return
    if (selectedLesson?.type === 'video' || selectedLesson?.isCompleted) return

    const timer = setTimeout(() => {
      handleCompleteLesson(selectedModule.id, selectedLesson.id)
    }, 5000)

    return () => clearTimeout(timer)
  }, [
    handleCompleteLesson,
    selectedLesson?.id,
    selectedLesson?.isCompleted,
    selectedLesson?.type,
    selectedModule?.id,
    showModularFlow,
  ])

  const handleVideoCompleted = useCallback(async () => {
    if (!selectedModule?.id || !selectedLesson?.id) return
    const completed = await handleCompleteLesson(selectedModule.id, selectedLesson.id)
    if (!completed || !canPromptSurveyAfterVideo) return

    setSurveyPromptVisible(true)
    trackEvent('survey_end_prompt_viewed', {
      content_type: 'survey',
      content_id: currentSurvey?.id,
      content_name: currentSurvey?.title,
      course_id: detail?.id,
      course_name: detail?.title,
      source: 'course_video_end',
    })
  }, [
    canPromptSurveyAfterVideo,
    currentSurvey,
    detail?.id,
    detail?.title,
    handleCompleteLesson,
    selectedLesson?.id,
    selectedModule?.id,
  ])

  useEffect(() => {
    if (!showModularFlow || !detail?.id) return

    const config = contentConfig[type]
    if (!config) return

    const eventName = `${config.analyticsPrefix}_view`
    trackOnce(`${eventName}:${detail.id}`, () => {
      trackEvent(eventName, {
        content_id: detail.id,
        content_name: detail.title,
        content_type: type,
        ...(type === 'courses'
          ? {
            course_id: detail.id,
            course_name: detail.title,
          }
          : {}),
      })
    })
  }, [contentConfig, detail?.id, detail?.title, showModularFlow, trackOnce, type])

  useEffect(() => {
    if (!showModularFlow || !detail?.id || !selectedModule?.id) return

    const config = contentConfig[type]
    if (!config) return

    const eventName = `${config.analyticsPrefix}_start`
    trackOnce(`${eventName}:${detail.id}:${selectedModule.id}`, () => {
      trackEvent(eventName, {
        content_id: detail.id,
        content_name: detail.title,
        content_type: type,
        module_id: selectedModule.id,
        module_title: selectedModule.title,
        ...(type === 'courses'
          ? {
            course_id: detail.id,
            course_name: detail.title,
          }
          : {}),
      })
    })
  }, [contentConfig, detail?.id, detail?.title, selectedModule?.id, selectedModule?.title, showModularFlow, trackOnce, type])

  useEffect(() => {
    if (!showModularFlow || !detail?.id || !selectedModule?.id || !selectedModule?.completed) return

    const config = contentConfig[type]
    if (!config) return

    const eventName = `${config.analyticsPrefix}_complete`
    trackOnce(`${eventName}:${detail.id}:${selectedModule.id}`, () => {
      trackEvent(eventName, {
        content_id: detail.id,
        content_name: detail.title,
        content_type: type,
        module_id: selectedModule.id,
        module_title: selectedModule.title,
        progress_percent: detail?.completionPercent,
        ...(type === 'courses'
          ? {
            course_id: detail.id,
            course_name: detail.title,
          }
          : {}),
      })
    })
  }, [contentConfig, detail?.completionPercent, detail?.id, detail?.title, selectedModule?.completed, selectedModule?.id, selectedModule?.title, showModularFlow, trackOnce, type])

  const modularSubtitle = useMemo(() => {
    if (!showModularFlow) return null

    const parts = [`${detail?.moduleCount || 0} modules`]
    if (detail?.duration) {
      parts.push(`${detail.duration} total`)
    }

    return parts.join(' • ')
  }, [detail?.duration, detail?.moduleCount, showModularFlow])

  const statsCards = useMemo(() => ([
    {
      icon: 'modules-stat',
      label: 'Modules',
      value: `${detail?.moduleCount || 0}`,
    },
    {
      icon: 'complete-stat',
      label: 'Complete',
      value: `${detail?.completionPercent || 0}%`,
    },
  ]), [detail?.completionPercent, detail?.moduleCount])

  const tabsProps = useMemo(() => ({
    actionHint: portalConfig.id === 'accelerator' ? null : currentQuiz?.hint || null,
    canGetCertificate: portalConfig.id !== 'accelerator' && !!currentQuiz?.canGetCertificate,
    canStartTest: !!currentQuiz?.canStartTest,
    hasQuiz: !!currentQuiz?.hasQuiz,
    category: detail?.category,
    certificateData: portalConfig.id === 'accelerator' ? null : currentQuiz?.certificate?.certificateData || null,
    certificateApproval: portalConfig.id === 'accelerator' ? null : currentQuiz?.certificateApproval || null,
    certificateFileName: `${currentQuiz?.certificate?.certificateData?.courseName || detail?.title || 'certificate'}.pdf`,
    courseType: detail?.type,
    canOpenSurvey: !!currentSurvey,
    handleStartQuiz: () => selectedModule?.id ? history.push(portalConfig.buildQuizRoute(selectedModule.id)) : null,
    handleOpenSurvey,
    lesson: selectedLesson,
    locale,
    moduleResources,
    surveyActionLabel: currentSurvey?.completed
      ? t('survey_completed_action')
      : currentSurvey?.required
        ? t('survey_complete_required')
        : t('survey_open'),
    surveyCompleted: !!currentSurvey?.completed,
    subtitle: modularSubtitle,
    title: detail?.title,
    transcriptLanguage,
  }), [
    currentQuiz?.canGetCertificate,
    currentQuiz?.canStartTest,
    currentQuiz?.certificate?.certificateData,
    currentQuiz?.certificateApproval,
    currentQuiz?.hint,
    currentQuiz?.hasQuiz,
    currentSurvey,
    detail?.category,
    detail?.title,
    detail?.type,
    modularSubtitle,
    moduleResources,
    history,
    handleOpenSurvey,
    locale,
    portalConfig,
    selectedLesson,
    selectedModule?.id,
    t,
    transcriptLanguage,
  ])

  const resourceCardProps = useMemo(() => ({
    lesson: selectedLesson,
    resources,
    subtitle: selectedLesson?.title ? `Resources for ${selectedLesson.title}` : null,
    title: 'Resources',
  }), [resources, selectedLesson])

  return {
    loading,
    error,
    retry: showModularFlow ? loadModularDetails : loadLegacyDetails,
    showModularFlow,
    detail,
    legacyCourse,
    selectedLesson,
    selectedModule,
    currentQuiz,
    currentSurvey,
    resources,
    modularSubtitle,
    statsCards,
    surveyEndPrompt: {
      visible: surveyPromptVisible && !!currentSurvey,
      survey: currentSurvey,
      onDismiss: handleDismissSurveyPrompt,
      onOpen: handleOpenSurveyFromPrompt,
    },
    tabsProps,
    resourceCardProps,
    userId: user?.id,
    trailContext,
    handleBackToCourses,
    handleBackToTrail,
    handleNextTrailItem: () => navigateTrailItem(trailContext?.nextItem),
    handlePreviousTrailItem: () => navigateTrailItem(trailContext?.previousItem),
    handleSelectLesson: lessonItem => setSelectedLessonId(lessonItem?.id || null),
    handleToggleFavorite,
    handleVideoCompleted,
  }
}
